// Forecast grid — every ACTIVE budget line for the FY (lines with no rows in
// the latest snapshot, e.g. just created, render with empty rows per §1
// get_forecast_grid), each with vendor/detail rows × 12 fiscal periods and
// derived quarter/annual cells. Editing is local; Save prompts for a
// snapshot reason and POSTs the COMPLETE grid (full-replace semantics, §6):
// every line is included even when untouched, because an omitted line would
// zero out in the new snapshot.
//
// Detail rows are typed (workbook-parity §4): `standard` keeps the
// vendor + label + 12-amounts grid; `travel`/`travel_other` rows carry trip
// meta (attendees, theme, purpose, destination, people, nights, frequency)
// in an inline-expandable editor with no vendor; `temp_labor` rows are a
// calculator — amounts derive client-side from bill rate × hours/week ×
// the 4-5-4 weeks pattern until an amount cell is hand-edited, which flips
// `manual_override` (undone by Recalc). Snapshots store plain amounts; the
// calculator inputs ride along in `detail_meta`.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TOKENS, MONO_FONT, NUM_STYLE } from '../reconciliation/tokens';
import { Icon, Modal, Tone, styles } from '../reconciliation/primitives';
import { fetchVendors, fetchUsers } from '../api';
import {
  createLine,
  fetchForecast,
  fetchSnapshots,
  saveForecast,
  updateLine,
} from './api';

// P1 = February … P12 = January (Feb-start fiscal calendar).
const PERIOD_MONTHS = ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
const PERIODS = Array.from({ length: 12 }, (_, i) => String(i + 1));

// Working weeks per fiscal period — keep in sync with
// src/budget/domain/fiscal.py::WEEKS_PER_PERIOD: the workbook's Temp Labor
// row 1, a (4,5,4)×4 = 52-week year, NOT the NRF 4-5-4 retail calendar
// (which inserts a 53rd week in some years).
const WEEKS_PER_PERIOD = [4, 5, 4, 4, 5, 4, 4, 5, 4, 4, 5, 4];

const DETAIL_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'travel', label: 'Travel' },
  { value: 'travel_other', label: 'Travel · Other (conference)' },
  { value: 'temp_labor', label: 'Temp Labor' },
];
const TYPE_BADGES = { travel: 'Travel', travel_other: 'Conference', temp_labor: 'Temp Labor' };

// Expanded-editor fields per type. Purpose (travel) and job title
// (temp_labor) live in the main row's Detail column — they double as the
// row's detail_label, mirroring the importer's label rule.
const TRAVEL_META_FIELDS = [
  { key: 'attendees', label: 'Attendees', wide: true },
  { key: 'theme', label: 'Theme', wide: true },
  { key: 'destination', label: 'Destination', wide: true },
  { key: 'people', label: 'People', numeric: true },
  { key: 'nights', label: 'Nights', numeric: true },
  { key: 'frequency_or_month', label: 'Freq / month' },
];
const TEMP_LABOR_META_FIELDS = [
  { key: 'resource_need', label: 'Resource need', wide: true },
  { key: 'contractor', label: 'Contractor', wide: true },
  { key: 'bill_rate', label: 'Bill rate', numeric: true },
  { key: 'hours_week', label: 'Hours / week', numeric: true },
];

function isTravelType(t) {
  return t === 'travel' || t === 'travel_other';
}

// Vendor | Detail | 4 × (3 periods + Q) | Annual | remove
const TOTAL_COLS = 2 + 16 + 1 + 1;

// Stable React keys for editable rows (server rows have no row id).
let rowKeySeq = 0;
function nextRowKey() {
  rowKeySeq += 1;
  return `r${rowKeySeq}`;
}

function parseAmount(v) {
  if (v == null || v === '') return 0;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? 0 : n;
}

function fmtTotal(n) {
  const s = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${s})` : s;
}

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('en-US');
}

// rate × hours × weeks per period — the calculator behind temp_labor rows.
// Mirrors domain/fiscal.py::temp_labor_amounts; the backend re-checks the
// same product on save (±0.01) but never originates amounts.
function computedAmounts(meta) {
  const rate = parseAmount(meta && meta.bill_rate);
  const hours = parseAmount(meta && meta.hours_week);
  return Object.fromEntries(
    PERIODS.map((p, i) => {
      const v = rate * hours * WEEKS_PER_PERIOD[i];
      return [p, v ? v.toFixed(2) : ''];
    })
  );
}

function majorityType(rows) {
  const counts = {};
  rows.forEach((r) => {
    counts[r.detail_type] = (counts[r.detail_type] || 0) + 1;
  });
  let best = 'standard';
  let n = 0;
  Object.entries(counts).forEach(([t, c]) => {
    if (c > n) {
      best = t;
      n = c;
    }
  });
  return best;
}

// Typed rows derive their detail_label from meta on save, exactly like the
// importer (travel: purpose else theme; temp_labor: job title).
function saveLabel(row) {
  const m = row.detail_meta || {};
  if (isTravelType(row.detail_type)) return m.purpose || m.theme || null;
  if (row.detail_type === 'temp_labor') return m.job_title || null;
  return row.detail_label || null;
}

// Drop empty meta values so an untouched typed row vanishes on save like an
// empty standard row does (the backend persists a zero marker only for rows
// with NON-empty meta). temp_labor always keeps manual_override and sends
// rate/hours as strings, matching the importer's meta shape.
function saveMeta(row) {
  if (!row.detail_meta) return null;
  const out = {};
  Object.entries(row.detail_meta).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') return;
    out[k] = v;
  });
  if (isTravelType(row.detail_type)) {
    ['people', 'nights'].forEach((k) => {
      if (out[k] != null && !Number.isNaN(Number(out[k]))) out[k] = Number(out[k]);
    });
  }
  if (row.detail_type === 'temp_labor') {
    out.manual_override = Boolean(row.detail_meta.manual_override);
    if (out.bill_rate != null) out.bill_rate = String(out.bill_rate);
    if (out.hours_week != null) out.hours_week = String(out.hours_week);
  }
  return Object.keys(out).length ? out : null;
}

function rowQuarter(row, q) {
  let t = 0;
  for (let p = (q - 1) * 3 + 1; p <= q * 3; p += 1) t += parseAmount(row.amounts[String(p)]);
  return t;
}

function rowAnnual(row) {
  return PERIODS.reduce((t, p) => t + parseAmount(row.amounts[p]), 0);
}

function linePeriod(line, p) {
  return line.rows.reduce((t, r) => t + parseAmount(r.amounts[p]), 0);
}

function lineQuarter(line, q) {
  return line.rows.reduce((t, r) => t + rowQuarter(r, q), 0);
}

function lineAnnual(line) {
  return line.rows.reduce((t, r) => t + rowAnnual(r), 0);
}

function VendorCombobox({ vendors, valueName, onPick }) {
  const [query, setQuery] = useState(valueName || '');
  const [open, setOpen] = useState(false);

  // Re-sync when the row's vendor changes from outside (e.g. grid reload).
  useEffect(() => {
    setQuery(valueName || '');
  }, [valueName]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? vendors.filter((v) => v.name.toLowerCase().includes(q)) : vendors;
    return list.slice(0, 30);
  }, [vendors, query]);

  return (
    <div className="bg-combobox">
      <input
        className="fc-text"
        value={query}
        placeholder="Vendor…"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (e.target.value === '') onPick(null);
        }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
      />
      {open && matches.length > 0 && (
        <div className="bg-combobox-menu">
          {matches.map((v) => (
            <div
              key={v.id}
              className="bg-combobox-opt"
              onMouseDown={() => {
                onPick(v);
                setQuery(v.name);
                setOpen(false);
              }}
            >
              {v.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AddLineModal({ fiscalYear, users, onClose, onCreated, onToast }) {
  const [form, setForm] = useState({
    category: '',
    name: '',
    owner_id: '',
    spend_type: 'base',
    internal_order: '',
    matter_codes: '',
    comments: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const body = {
        fiscal_year: fiscalYear,
        owner_id: form.owner_id || null,
        category: form.category.trim(),
        name: form.name.trim(),
        spend_type: form.spend_type,
        matter_codes: form.matter_codes
          .split(/[,/]/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      if (form.internal_order.trim()) body.internal_order = form.internal_order.trim();
      if (form.comments.trim()) body.comments = form.comments.trim();
      await createLine(body);
      onToast({ text: `Added budget line "${body.name}"`, tone: 'good' });
      onCreated();
    } catch (err) {
      onToast({
        text: err.response?.data?.detail || err.message || 'Failed to create budget line',
        tone: 'miss',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 };
  const labelStyle = {
    fontFamily: MONO_FONT,
    fontSize: 9.5,
    color: TOKENS.ink3,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
  };

  return (
    <Modal
      title="Add budget line"
      subtitle={`FY${fiscalYear}`}
      onClose={onClose}
      footer={
        <>
          <button style={{ ...styles.ghostBtn, marginLeft: 'auto' }} onClick={onClose}>
            Cancel
          </button>
          <button
            style={styles.primaryBtn}
            disabled={submitting || !form.category.trim() || !form.name.trim()}
            onClick={handleSubmit}
          >
            {submitting ? 'Adding…' : 'Add line'}
          </button>
        </>
      }
    >
      <div style={fieldStyle}>
        <span style={labelStyle}>Category</span>
        <input className="fc-text" value={form.category} onChange={set('category')} placeholder="e.g. Controversy" />
      </div>
      <div style={fieldStyle}>
        <span style={labelStyle}>Name</span>
        <input className="fc-text" value={form.name} onChange={set('name')} placeholder="e.g. Transfer Pricing Report - Canada" />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <span style={labelStyle}>Owner</span>
          <select className="fc-text" value={form.owner_id} onChange={set('owner_id')}>
            <option value="">— none —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <span style={labelStyle}>Spend type</span>
          <select className="fc-text" value={form.spend_type} onChange={set('spend_type')}>
            <option value="base">Base</option>
            <option value="one_time">One-Time</option>
          </select>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <span style={labelStyle}>Internal order</span>
          <input className="fc-text code" value={form.internal_order} onChange={set('internal_order')} placeholder="e.g. TAX1515" />
        </div>
        <div style={{ ...fieldStyle, flex: 1 }}>
          <span style={labelStyle}>Matter codes</span>
          <input className="fc-text code" value={form.matter_codes} onChange={set('matter_codes')} placeholder="e.g. 29122 / 29864" />
        </div>
      </div>
      <div style={fieldStyle}>
        <span style={labelStyle}>Comments</span>
        <input className="fc-text" value={form.comments} onChange={set('comments')} />
      </div>
    </Modal>
  );
}

function SaveModal({ onClose, onSave, saving }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      title="Save forecast"
      subtitle="Creates a new snapshot — prior snapshots are kept"
      onClose={onClose}
      footer={
        <>
          <button style={{ ...styles.ghostBtn, marginLeft: 'auto' }} onClick={onClose}>
            Cancel
          </button>
          <button
            style={styles.primaryBtn}
            disabled={saving || !reason.trim()}
            onClick={() => onSave(reason.trim())}
          >
            {saving ? 'Saving…' : 'Save snapshot'}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span
          style={{
            fontFamily: MONO_FONT,
            fontSize: 9.5,
            color: TOKENS.ink3,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
          }}
        >
          Snapshot reason
        </span>
        <input
          className="fc-text"
          autoFocus
          value={reason}
          placeholder="e.g. Q2 reforecast"
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function SnapshotHistoryList({ snapshots }) {
  return (
    <div className="fc-snapshots">
      <div className="fc-snapshots-title">Snapshot history</div>
      {snapshots.length === 0 ? (
        <div style={{ fontSize: 11.5, color: TOKENS.ink3 }}>
          No snapshots yet — saving the grid creates the first one.
        </div>
      ) : (
        <div className="budget-table-wrap">
          <table className="budget-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Created</th>
                <th style={{ textAlign: 'left' }}>Reason</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((s) => (
                <tr key={s.id} className="budget-row">
                  <td>
                    <span style={{ ...NUM_STYLE, fontSize: 10.5 }}>{fmtDate(s.created_at)}</span>
                  </td>
                  <td>{s.snapshot_reason || '—'}</td>
                  <td className="bt-num">{fmtTotal(Number(s.total || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// "+ Add detail row" with a row-type picker — offered on every line,
// defaulting to the line's majority existing type (so the Travel line adds
// travel rows, Loan Staff adds temp_labor, everything else standard).
function AddRowControl({ line, onAdd }) {
  const [type, setType] = useState(() => majorityType(line.rows));
  return (
    <div className="fc-add-wrap">
      <button className="fc-add-row" onClick={() => onAdd(type)}>
        + Add detail row
      </button>
      <select
        className="fc-add-type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        title="Row type for new detail rows"
      >
        {DETAIL_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function MetaEditorRow({ row, fields, onField }) {
  const meta = row.detail_meta || {};
  return (
    <tr className="fc-meta-row">
      <td colSpan={TOTAL_COLS}>
        <div className="fc-meta-grid">
          {fields.map((f) => (
            <label key={f.key} className={`fc-meta-field${f.wide ? ' wide' : ''}`}>
              <span className="fc-meta-label">{f.label}</span>
              <input
                className={f.numeric ? 'fc-text num fc-meta-num' : 'fc-text'}
                inputMode={f.numeric ? 'decimal' : undefined}
                value={meta[f.key] ?? ''}
                onChange={(e) => onField(f.key, e.target.value)}
              />
            </label>
          ))}
        </div>
      </td>
    </tr>
  );
}

export default function ForecastView({ fiscalYear, onToast }) {
  const [lines, setLines] = useState([]);
  const [snapshotMeta, setSnapshotMeta] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // 'add-line' | 'save' | null
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchForecast(fiscalYear);
      setSnapshotMeta(res.data?.snapshot || null);
      setLines(
        (res.data?.lines || []).map((l) => ({
          ...l,
          rows: (l.rows || []).map((r) => ({
            _key: nextRowKey(),
            vendor_id: r.vendor_id || null,
            vendor_name: r.vendor_name || '',
            detail_label: r.detail_label || '',
            detail_type: r.detail_type || 'standard',
            detail_meta: r.detail_meta ? { ...r.detail_meta } : null,
            _open: false,
            amounts: { ...(r.amounts || {}) },
          })),
        }))
      );
      setDirty(false);
    } catch (err) {
      if (err.response?.status === 404) {
        setLines([]);
        setSnapshotMeta(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load forecast');
        setLines([]);
      }
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);

  const loadSnapshots = useCallback(async () => {
    try {
      const res = await fetchSnapshots(fiscalYear);
      setSnapshots(res.data?.snapshots || []);
    } catch {
      setSnapshots([]);
    }
  }, [fiscalYear]);

  useEffect(() => {
    load();
    loadSnapshots();
  }, [load, loadSnapshots]);

  useEffect(() => {
    let cancelled = false;
    fetchVendors()
      .then((res) => {
        if (!cancelled) setVendors(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    fetchUsers()
      .then((res) => {
        if (!cancelled) setUsers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Local grid mutations ─────────────────────────────────────────────
  const patchRow = (lineId, key, patch) => {
    setLines((ls) =>
      ls.map((l) =>
        l.id !== lineId
          ? l
          : { ...l, rows: l.rows.map((r) => (r._key === key ? { ...r, ...patch } : r)) }
      )
    );
    setDirty(true);
  };

  const setRowAmount = (lineId, key, period, value) => {
    setLines((ls) =>
      ls.map((l) =>
        l.id !== lineId
          ? l
          : {
              ...l,
              rows: l.rows.map((r) => {
                if (r._key !== key) return r;
                const next = { ...r, amounts: { ...r.amounts, [period]: value } };
                // Hand-editing a computed temp_labor cell switches the row
                // to manual override (Recalc undoes it).
                if (
                  r.detail_type === 'temp_labor' &&
                  r.detail_meta &&
                  !r.detail_meta.manual_override
                ) {
                  next.detail_meta = { ...r.detail_meta, manual_override: true };
                }
                return next;
              }),
            }
      )
    );
    setDirty(true);
  };

  const setMetaField = (lineId, key, field, value) => {
    setLines((ls) =>
      ls.map((l) =>
        l.id !== lineId
          ? l
          : {
              ...l,
              rows: l.rows.map((r) => {
                if (r._key !== key) return r;
                const meta = { ...(r.detail_meta || {}), [field]: value };
                const next = { ...r, detail_meta: meta };
                if (
                  r.detail_type === 'temp_labor' &&
                  !meta.manual_override &&
                  (field === 'bill_rate' || field === 'hours_week')
                ) {
                  next.amounts = computedAmounts(meta);
                }
                return next;
              }),
            }
      )
    );
    setDirty(true);
  };

  const toggleRowOpen = (lineId, key) => {
    // Display state only — does not dirty the grid.
    setLines((ls) =>
      ls.map((l) =>
        l.id !== lineId
          ? l
          : { ...l, rows: l.rows.map((r) => (r._key === key ? { ...r, _open: !r._open } : r)) }
      )
    );
  };

  const recalcTempLabor = (lineId, key) => {
    setLines((ls) =>
      ls.map((l) =>
        l.id !== lineId
          ? l
          : {
              ...l,
              rows: l.rows.map((r) => {
                if (r._key !== key) return r;
                const meta = { ...(r.detail_meta || {}), manual_override: false };
                return { ...r, detail_meta: meta, amounts: computedAmounts(meta) };
              }),
            }
      )
    );
    setDirty(true);
  };

  const addRow = (lineId, detailType = 'standard') => {
    const row = {
      _key: nextRowKey(),
      vendor_id: null,
      vendor_name: '',
      detail_label: '',
      detail_type: detailType,
      detail_meta: null,
      _open: false,
      amounts: {},
    };
    if (isTravelType(detailType)) {
      row.detail_meta = {
        attendees: '',
        theme: '',
        purpose: '',
        destination: '',
        people: null,
        nights: null,
        frequency_or_month: '',
      };
      row._open = true;
    } else if (detailType === 'temp_labor') {
      row.detail_meta = {
        resource_need: '',
        job_title: '',
        contractor: '',
        bill_rate: '',
        hours_week: '',
        manual_override: false,
      };
      row._open = true;
    }
    setLines((ls) =>
      ls.map((l) => (l.id !== lineId ? l : { ...l, rows: [...l.rows, row] }))
    );
    setDirty(true);
  };

  const removeRow = (lineId, key) => {
    setLines((ls) =>
      ls.map((l) => (l.id !== lineId ? l : { ...l, rows: l.rows.filter((r) => r._key !== key) }))
    );
    setDirty(true);
  };

  // ── Line meta (saved server-side immediately, not part of the grid) ──
  const setLineLocal = (lineId, fields) => {
    setLines((ls) => ls.map((l) => (l.id === lineId ? { ...l, ...fields } : l)));
  };

  const patchLineMeta = async (lineId, fields) => {
    setLineLocal(lineId, fields);
    try {
      await updateLine(lineId, fields);
    } catch (err) {
      onToast({
        text: err.response?.data?.detail || err.message || 'Failed to update line',
        tone: 'miss',
      });
      load();
    }
  };

  const closeLine = async (line) => {
    if (
      !window.confirm(
        `Close budget line "${line.name}"? It drops out of the forecast grid but keeps its history on the dashboard.`
      )
    ) {
      return;
    }
    try {
      await updateLine(line.id, { status: 'closed' });
      onToast({ text: `Closed "${line.name}"`, tone: 'good' });
      await load();
    } catch (err) {
      onToast({
        text: err.response?.data?.detail || err.message || 'Failed to close line',
        tone: 'miss',
      });
    }
  };

  // ── Save (full-replace POST /forecast/) ──────────────────────────────
  const handleSave = async (reason) => {
    setSaving(true);
    try {
      const payload = lines.map((l) => ({
        budget_line_id: l.id,
        rows: l.rows.map((r) => ({
          vendor_id: r.vendor_id || null,
          detail_label: saveLabel(r),
          detail_type: r.detail_type || 'standard',
          detail_meta: saveMeta(r),
          amounts: Object.fromEntries(PERIODS.map((p) => [p, parseAmount(r.amounts[p]).toFixed(2)])),
        })),
      }));
      await saveForecast(fiscalYear, reason, payload);
      onToast({ text: 'Forecast saved — new snapshot created', tone: 'good' });
      setModal(null);
      await load();
      await loadSnapshots();
    } catch (err) {
      onToast({
        text: err.response?.data?.detail || err.message || 'Save failed',
        tone: 'miss',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading && !lines.length) {
    return (
      <div className="budget-empty">
        <div className="title">Loading forecast…</div>
        <div className="sub">Fetching the FY{fiscalYear} grid from the latest snapshot.</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="budget-empty">
        <div className="title">Couldn't load forecast</div>
        <div className="sub">{error}</div>
      </div>
    );
  }

  return (
    <>
      <div className="fc-toolbar">
        <span className="budget-snapmeta" style={{ marginLeft: 0 }}>
          {snapshotMeta
            ? `Editing from snapshot: ${snapshotMeta.reason || 'unnamed'} · ${fmtDate(snapshotMeta.created_at)}`
            : 'No snapshot yet — saving creates the first one'}
        </span>
        {dirty && <Tone tone="soft">unsaved changes</Tone>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button style={styles.ghostBtn} onClick={() => setModal('add-line')}>
            <Icon d="M12 5v14M5 12h14" size={12} sw={2} />
            Add budget line
          </button>
          <button style={styles.primaryBtn} disabled={!lines.length} onClick={() => setModal('save')}>
            <Icon d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8M7 3v5h8" size={12} sw={2} />
            Save forecast
          </button>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="budget-empty">
          <div className="title">No active budget lines for FY{fiscalYear}</div>
          <div className="sub">Add a budget line to start forecasting.</div>
        </div>
      ) : (
        <div className="budget-table-wrap budget-forecast-wrap">
          <table className="budget-table">
            <thead>
              <tr>
                <th className="bt-name" style={{ minWidth: 170 }}>
                  Vendor
                </th>
                <th style={{ textAlign: 'left', minWidth: 130 }}>Detail</th>
                {[1, 2, 3, 4].map((q) => (
                  <React.Fragment key={q}>
                    {[0, 1, 2].map((i) => (
                      <th key={`p${(q - 1) * 3 + i + 1}`} className={i === 0 ? 'group-edge' : ''}>
                        {PERIOD_MONTHS[(q - 1) * 3 + i]}
                      </th>
                    ))}
                    <th>Q{q}</th>
                  </React.Fragment>
                ))}
                <th className="group-edge">Annual</th>
                <th aria-label="Row actions" />
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <React.Fragment key={line.id}>
                  <tr className="fc-line-head">
                    <td colSpan={2}>
                      <div className="fc-line-meta">
                        <div className="fc-line-title">
                          <span>{line.name}</span>
                          <span className="cat">{line.category}</span>
                        </div>
                        <div className="fc-line-controls">
                          <select
                            value={line.spend_type || 'base'}
                            onChange={(e) => patchLineMeta(line.id, { spend_type: e.target.value })}
                          >
                            <option value="base">Base</option>
                            <option value="one_time">One-Time</option>
                          </select>
                          <input
                            className="fc-comments"
                            placeholder="Comments…"
                            value={line.comments || ''}
                            onChange={(e) => setLineLocal(line.id, { comments: e.target.value })}
                            onBlur={(e) => patchLineMeta(line.id, { comments: e.target.value || null })}
                          />
                          <button
                            className="fc-close-line"
                            onClick={() => closeLine(line)}
                            title="Close this budget line — it drops out of the forecast grid"
                          >
                            Close line
                          </button>
                        </div>
                      </div>
                    </td>
                    {[1, 2, 3, 4].map((q) => (
                      <React.Fragment key={q}>
                        {[0, 1, 2].map((i) => {
                          const p = String((q - 1) * 3 + i + 1);
                          return (
                            <td key={`p${p}`} className={`bt-num fc-head-num${i === 0 ? ' group-edge' : ''}`}>
                              {fmtTotal(linePeriod(line, p))}
                            </td>
                          );
                        })}
                        <td className="bt-num fc-head-num fc-derived">{fmtTotal(lineQuarter(line, q))}</td>
                      </React.Fragment>
                    ))}
                    <td className="bt-num fc-head-num fc-derived group-edge">
                      {fmtTotal(lineAnnual(line))}
                    </td>
                    <td />
                  </tr>
                  {line.rows.map((row) => {
                    const typed = row.detail_type && row.detail_type !== 'standard';
                    const isTemp = row.detail_type === 'temp_labor';
                    const calc = isTemp && row.detail_meta && !row.detail_meta.manual_override;
                    return (
                      <React.Fragment key={row._key}>
                        <tr className="budget-row">
                          <td>
                            {typed ? (
                              <div className="fc-type-cell">
                                <button
                                  className="fc-meta-toggle"
                                  onClick={() => toggleRowOpen(line.id, row._key)}
                                  title={row._open ? 'Hide row details' : 'Edit row details'}
                                >
                                  <Icon d={row._open ? 'M6 9l6 6 6-6' : 'M9 6l6 6-6 6'} size={10} sw={2} />
                                  <span className="fc-type-badge">{TYPE_BADGES[row.detail_type]}</span>
                                </button>
                                {isTemp &&
                                  (row.detail_meta?.manual_override ? (
                                    <>
                                      <span
                                        className="fc-manual-badge"
                                        title="Amounts edited by hand — no longer bill rate × hours/week × weeks"
                                      >
                                        manual
                                      </span>
                                      <button
                                        className="fc-recalc"
                                        onClick={() => recalcTempLabor(line.id, row._key)}
                                        title="Recalculate amounts from bill rate × hours/week × weeks"
                                      >
                                        Recalc
                                      </button>
                                    </>
                                  ) : (
                                    <span className="fc-meta-sum num">
                                      {parseAmount(row.detail_meta?.bill_rate)} × {parseAmount(row.detail_meta?.hours_week)}h/wk
                                    </span>
                                  ))}
                              </div>
                            ) : (
                              <VendorCombobox
                                vendors={vendors}
                                valueName={row.vendor_name}
                                onPick={(v) =>
                                  patchRow(line.id, row._key, {
                                    vendor_id: v ? v.id : null,
                                    vendor_name: v ? v.name : '',
                                  })
                                }
                              />
                            )}
                          </td>
                          <td>
                            {typed ? (
                              <input
                                className="fc-text"
                                value={(isTemp ? row.detail_meta?.job_title : row.detail_meta?.purpose) ?? ''}
                                placeholder={isTemp ? 'Job title' : 'Purpose / objective'}
                                onChange={(e) =>
                                  setMetaField(line.id, row._key, isTemp ? 'job_title' : 'purpose', e.target.value)
                                }
                              />
                            ) : (
                              <input
                                className="fc-text"
                                value={row.detail_label}
                                placeholder="Detail label"
                                onChange={(e) => patchRow(line.id, row._key, { detail_label: e.target.value })}
                              />
                            )}
                          </td>
                          {[1, 2, 3, 4].map((q) => (
                            <React.Fragment key={q}>
                              {[0, 1, 2].map((i) => {
                                const p = String((q - 1) * 3 + i + 1);
                                return (
                                  <td key={`p${p}`} className={`bt-num${i === 0 ? ' group-edge' : ''}`}>
                                    <input
                                      className={`fc-input${calc ? ' fc-calc' : ''}`}
                                      inputMode="decimal"
                                      placeholder="0"
                                      title={
                                        calc
                                          ? 'Computed from bill rate × hours/week — editing switches to manual override'
                                          : undefined
                                      }
                                      value={row.amounts[p] ?? ''}
                                      onChange={(e) => setRowAmount(line.id, row._key, p, e.target.value)}
                                    />
                                  </td>
                                );
                              })}
                              <td className="bt-num fc-derived">{fmtTotal(rowQuarter(row, q))}</td>
                            </React.Fragment>
                          ))}
                          <td className="bt-num fc-derived group-edge">{fmtTotal(rowAnnual(row))}</td>
                          <td>
                            <button
                              className="fc-remove"
                              onClick={() => removeRow(line.id, row._key)}
                              title="Remove detail row"
                            >
                              <Icon d="M3 6h18M8 6V4h8v2m1 0v14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6" size={12} sw={1.8} />
                            </button>
                          </td>
                        </tr>
                        {typed && row._open && (
                          <MetaEditorRow
                            row={row}
                            fields={isTemp ? TEMP_LABOR_META_FIELDS : TRAVEL_META_FIELDS}
                            onField={(field, value) => setMetaField(line.id, row._key, field, value)}
                          />
                        )}
                      </React.Fragment>
                    );
                  })}
                  <tr>
                    <td colSpan={TOTAL_COLS} style={{ background: 'oklch(0.99 0.002 250)' }}>
                      <AddRowControl line={line} onAdd={(type) => addRow(line.id, type)} />
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SnapshotHistoryList snapshots={snapshots} />

      {modal === 'add-line' && (
        <AddLineModal
          fiscalYear={fiscalYear}
          users={users}
          onClose={() => setModal(null)}
          onCreated={() => {
            setModal(null);
            load();
          }}
          onToast={onToast}
        />
      )}
      {modal === 'save' && (
        <SaveModal onClose={() => setModal(null)} onSave={handleSave} saving={saving} />
      )}
    </>
  );
}
